import type { ColumnDef } from "@tanstack/react-table";
import { MoreHorizontal, Package, Eye } from "lucide-react";
import { Button } from "../../ui/button";
/* import { Checkbox } from "../../ui/checkbox"; */
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  /* DropdownMenuSeparator, */
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import {
  Card,
  CardContent
} from "../../ui/card";
import { Badge } from "../../ui/badge";
import { DataTable } from "../../ui/data-table/data-table";
import { DataTableColumnHeader } from "../../ui/data-table/data-table-column-header";
import { ExportMenu } from "../../ui/export-menu";
import { SummaryHeader } from "../../ui/SummaryHeader";
import { useRecentTransactions } from "../hooks/useRecentTransaction";
import type { Order } from "../services/recentTransacService";
import DataLoader from "../../../components/loaders/DataLoader";
import { formatCurrency } from "../../../utils/formatCurrency";


// Order type definition
type OrderStatus =
  | "Completed"
  | "Processing"
  | "Shipped"
  | "Cancelled"
  | "Refunded"
  | "Authorized"
  | "Paid"
  | "Replaced"
  | "Exchanged";



// Sample data


function OrderStatusBadge({ status }: { status: OrderStatus }) {
  const statusStyles = {
    Completed: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
    Processing: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
    Shipped: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300",
    Cancelled: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
    Refunded: "bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-300",
    Replaced: "bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-300",
    Authorized: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300",
    Paid: "bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-300",
    Exchanged: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
  };

  return (
    <Badge className={statusStyles[status]} variant="outline">
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  );
}

export const columns: ColumnDef<Order>[] = [
  /* {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && "indeterminate")
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
      />
    ),
    enableSorting: false,
    enableHiding: false,
  }, */
  {

    accessorKey: "orderNumber",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Order" />
    ),
    cell: ({ row }) => (
      <div className="flex min-w-40 items-center gap-2">
        <div className="h-8 w-8 rounded-full bg-zinc-100 flex items-center justify-center">
          <Package className="h-4 w-4 text-zinc-500" />
        </div>
        <div>
          <div className="font-medium">{row.getValue("orderNumber")}</div>
          <div className="text-xs text-muted-foreground">
            {row.original.customer}
          </div>
        </div>
      </div>
    ),
  },
  {
    accessorKey: "date",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Date" />
    ),
    cell: ({ row }) => {
      const date = new Date(row.getValue("date"));
      return <div>{date.toLocaleDateString()}</div>;
    },
  },
  {
    accessorKey: "status",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" showSortingOptions={false} />
    ),
    cell: ({ row }) => <OrderStatusBadge status={row.getValue("status")} />,
    filterFn: (row, id, value) => {
      return value.includes(row.getValue(id));
    },
  },
  {
    accessorKey: "quantity",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Qty" />
    ),
  },
  {
    accessorKey: "cogs",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="COGS" />
    ),
    cell: ({ row }) => {
      const raw = row.getValue("cogs") as number | string | null | undefined;

      if (raw === null || raw === undefined) {
        return <div className="text-muted-foreground italic">N/A</div>;
      }

      const amount =
        typeof raw === "number"
          ? raw
          : typeof raw === "string"
            ? parseFloat(raw)
            : NaN;

      if (isNaN(amount)) {
        return <div className="text-muted-foreground italic">N/A</div>;
      }

      const formatted = formatCurrency(amount);

      return <div className="font-medium">{formatted}</div>;
    }
  },
  {
    accessorKey: "total",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Total" />
    ),
    cell: ({ row }) => {
      const amount = Number.parseFloat(row.getValue("total"));
      const formatted = formatCurrency(amount);
      return <div className="font-medium">{formatted}</div>;
    },
  },
  {
    accessorKey: "paymentMethod",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Payment" />
    ),
  },
  {
    accessorKey: "channel",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Channel" />
    ),
  },
  {
    id: "actions",
    cell: ({ row }) => {
      const order = row.original;
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuItem
              onClick={() => navigator.clipboard.writeText(order.id)}
            >
              Copy order ID
            </DropdownMenuItem>
            {/* <DropdownMenuSeparator />
            <DropdownMenuItem>View details</DropdownMenuItem>
            <DropdownMenuItem>Update status</DropdownMenuItem>
            <DropdownMenuItem>Print invoice</DropdownMenuItem> */}
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];

export function OrdersTable() {
  const { data: orders = [], isLoading, error } = useRecentTransactions(40);


  if (error) return <div>Error loading data: {error.message}</div>;



  return (
    <>
      <SummaryHeader
        title="Recent Transactions"
      />
      <Card>
        <div className="flex flex-col sm:flex-row gap-2 p-4">
          <div className="w-full flex justify-end gap-2">
            <Button size="sm">
              <Eye className="mr-2 h-4 w-4" />
              View All
            </Button>
            <ExportMenu
              data={orders}
              filename="orders_data"
              buttonSize="sm"
              label=""
            />
          </div>
        </div>
        {isLoading ? (
          <DataLoader variant="orders-table" itemCount={5} />
        ) : (
          <CardContent className="p-4">
            <DataTable
              columns={columns}
              data={orders}
              searchColumn="orderNumber"
              searchPlaceholder="Search Order number.."
              initialPageSize={5}
            />
          </CardContent>
        )}
      </Card>
    </>
  );
}
